import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";

const STEPS = [
  { id: 1, mark: "01 · BIENVENIDO", title: "¿Cómo se llama tu marca?" },
  { id: 2, mark: "02 · INDUSTRIA", title: "¿En qué industria juegas?" },
  { id: 3, mark: "03 · AUDIENCIA", title: "¿A quién le hablas?" },
  { id: 4, mark: "04 · VOZ", title: "¿Qué tono usa tu marca?" },
  { id: 5, mark: "05 · TEMAS", title: "Temas y palabras prohibidas" },
  { id: 6, mark: "06 · LOGO", title: "Tu logo" },
  { id: 7, mark: "07 · FACTURACIÓN", title: "¿Necesitas factura?" },
  { id: 8, mark: "08 · LISTO", title: "Todo configurado" },
];

const INDUSTRIES = ["Restaurantes", "Retail", "Servicios profesionales", "Salud", "Educación",
  "Tecnología", "Inmobiliaria", "Belleza & Cuidado", "Finanzas", "Otro"];
const AUDIENCE_TYPES = [
  { id: "B2C", label: "B2C · vendo a personas" },
  { id: "B2B", label: "B2B · vendo a empresas" },
  { id: "mixto", label: "Mixto · ambos" },
];
const TONES = [
  { id: "casual", label: "Casual" },
  { id: "profesional", label: "Profesional" },
  { id: "edgy", label: "Edgy / atrevido" },
  { id: "amigable", label: "Cercano / familiar" },
];
const COUNTRIES = [
  { id: "MX", label: "México" }, { id: "AR", label: "Argentina" },
  { id: "CO", label: "Colombia" }, { id: "CL", label: "Chile" },
  { id: "PE", label: "Perú" }, { id: "ES", label: "España" }, { id: "US", label: "USA" },
];
const REGIMEN_FISCAL = [
  ["605", "Sueldos y Salarios"],
  ["612", "Persona Física con actividad empresarial"],
  ["616", "Sin obligaciones fiscales"],
  ["621", "Incorporación Fiscal"],
  ["626", "RESICO"],
  ["601", "General de Ley Personas Morales"],
  ["603", "Personas Morales con Fines no Lucrativos"],
];
const USOS_CFDI = [
  ["G03", "G03 · Gastos en general"],
  ["G01", "G01 · Adquisición de mercancías"],
  ["S01", "S01 · Sin efectos fiscales"],
  ["P01", "P01 · Por definir"],
];

export default function OnboardingWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    brand_name: "",
    industry: "",
    audience: "",
    audience_type: "B2C",
    country: "MX",
    tone: "casual",
    topics: "",
    banned_words: "",
    use_emojis: true,
    logo_url: "",
    language: "es-MX",
  });
  const [tax, setTax] = useState({
    needs_invoice: false, rfc: "", razon_social: "", regimen_fiscal: "612",
    cp_fiscal: "", uso_cfdi: "G03", fiscal_email: "",
  });
  const [generatingLogo, setGeneratingLogo] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/onboarding/status").then(({ data }) => {
      if (data.onboarded) nav("/dashboard", { replace: true });
    }).catch(() => {});
  }, [nav]);

  const set = (f, v) => setData((d) => ({ ...d, [f]: v }));
  const setT = (f, v) => setTax((t) => ({ ...t, [f]: v }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const generateLogo = async () => {
    if (!data.brand_name) return toast.error("Pon nombre de marca primero");
    setGeneratingLogo(true);
    try {
      const { data: img } = await api.post("/generate/image", {
        prompt: `Logo profesional moderno minimalista de la marca ${data.brand_name}, industria ${data.industry || "general"}, fondo blanco`,
      });
      set("logo_url", img.image_base64 ? `data:image/png;base64,${img.image_base64}` : (img.url || ""));
      toast.success("Logo generado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el logo");
    } finally { setGeneratingLogo(false); }
  };

  const finish = async (skipped = false) => {
    setSaving(true);
    try {
      if (!skipped) {
        await api.post("/onboarding/finish", {
          ...data,
          topics: data.topics.split(",").map((s) => s.trim()).filter(Boolean),
          banned_words: data.banned_words.split(",").map((s) => s.trim()).filter(Boolean),
        });
        await api.put("/invoices/tax-data", tax);
      } else {
        await api.post("/onboarding/skip");
      }
      toast.success("Listo, ya puedes empezar");
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const stepValid = () => {
    if (step === 1) return data.brand_name.trim().length >= 2;
    if (step === 2) return !!data.industry;
    if (step === 3) return !!data.audience_type && !!data.country;
    if (step === 4) return !!data.tone;
    if (step === 7) {
      if (!tax.needs_invoice) return true;
      return tax.rfc && tax.razon_social && tax.cp_fiscal && tax.regimen_fiscal && tax.uso_cfdi;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A] flex items-center justify-center p-4 md:p-8" data-testid="onboarding-wizard">
      <div className="w-full max-w-3xl">
        {/* Progress */}
        <div className="grid grid-cols-8 gap-1 mb-8">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={`h-1 ${s.id <= step ? "bg-[#0A0A0A]" : "bg-[#0A0A0A]/15"}`}
              data-testid={`onb-step-bar-${s.id}`}
            />
          ))}
        </div>

        {STEPS.filter((s) => s.id === step).map((s) => (
          <div key={s.id} className="bg-white border border-[#0A0A0A] p-8 md:p-12 brutal-shadow">
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{s.mark}</div>
            <h1 className="font-display text-3xl md:text-5xl mt-3 mb-8 leading-[0.95]">{s.title}</h1>

            {step === 1 && (
              <>
                <input
                  value={data.brand_name}
                  onChange={(e) => set("brand_name", e.target.value)}
                  placeholder="Ej: Café del Valle"
                  autoFocus
                  className="w-full bg-[#F4F4F0] border border-[#0A0A0A] px-4 py-4 font-mono-data text-base"
                  data-testid="onb-brand"
                />
                <p className="font-mono-data text-xs text-[#5C5C5C] mt-3">Puedes cambiar este nombre cuando quieras desde Settings.</p>
              </>
            )}

            {step === 2 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {INDUSTRIES.map((i) => (
                  <button
                    key={i}
                    onClick={() => set("industry", i)}
                    className={`p-3 border border-[#0A0A0A] font-mono-data text-sm ${data.industry === i ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
                    data-testid={`onb-industry-${i}`}
                  >{i}</button>
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-3 gap-2">
                  {AUDIENCE_TYPES.map((a) => (
                    <button key={a.id} onClick={() => set("audience_type", a.id)}
                      className={`p-3 border border-[#0A0A0A] font-mono-data text-sm text-left ${data.audience_type === a.id ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
                      data-testid={`onb-aud-${a.id}`}>{a.label}</button>
                  ))}
                </div>
                <div>
                  <label className="font-mono-mark text-[10px] text-[#5C5C5C]">DESCRIBE A TU CLIENTE IDEAL</label>
                  <textarea value={data.audience} onChange={(e) => set("audience", e.target.value)}
                    rows={3} placeholder="Ej: Mujeres profesionistas 28-45, oficinistas, CDMX/MTY, valoran calidad…"
                    className="w-full mt-2 bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" />
                </div>
                <div>
                  <label className="font-mono-mark text-[10px] text-[#5C5C5C]">PAÍS PRINCIPAL</label>
                  <select value={data.country} onChange={(e) => set("country", e.target.value)}
                    className="w-full mt-2 bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" data-testid="onb-country">
                    {COUNTRIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-2">
                  {TONES.map((t) => (
                    <button key={t.id} onClick={() => set("tone", t.id)}
                      className={`p-4 border border-[#0A0A0A] font-display text-xl text-left ${data.tone === t.id ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
                      data-testid={`onb-tone-${t.id}`}>{t.label}</button>
                  ))}
                </div>
                <label className="flex items-center gap-2 font-mono-data text-sm">
                  <input type="checkbox" checked={data.use_emojis} onChange={(e) => set("use_emojis", e.target.checked)} />
                  Usar emojis en los posts
                </label>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <div>
                  <label className="font-mono-mark text-[10px] text-[#5C5C5C]">TEMAS DE TU MARCA (separados por coma)</label>
                  <input value={data.topics} onChange={(e) => set("topics", e.target.value)}
                    placeholder="Ej: café especialidad, oficinas, productividad"
                    className="w-full mt-2 bg-[#F4F4F0] border border-[#0A0A0A] px-3 py-3 font-mono-data text-sm" />
                </div>
                <div>
                  <label className="font-mono-mark text-[10px] text-[#5C5C5C]">PALABRAS PROHIBIDAS (la IA las evitará)</label>
                  <input value={data.banned_words} onChange={(e) => set("banned_words", e.target.value)}
                    placeholder="Ej: barato, descuento agresivo, urgente urgente"
                    className="w-full mt-2 bg-[#F4F4F0] border border-[#0A0A0A] px-3 py-3 font-mono-data text-sm" />
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-4">
                {data.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img src={data.logo_url} alt="logo" className="w-32 h-32 object-contain bg-white border border-[#0A0A0A]" />
                    <button onClick={generateLogo} disabled={generatingLogo}
                      className="font-mono-mark text-[10px] px-4 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white">
                      {generatingLogo ? "GENERANDO..." : "GENERAR OTRO"}
                    </button>
                  </div>
                ) : (
                  <button onClick={generateLogo} disabled={generatingLogo}
                    className="bg-[#FFD600] text-[#0A0A0A] px-5 py-3 font-mono-mark text-[10px] border-2 border-[#0A0A0A] hover-brutal flex items-center gap-2"
                    data-testid="onb-gen-logo">
                    <Sparkles className="w-4 h-4" /> {generatingLogo ? "GENERANDO LOGO IA..." : "GENERAR LOGO CON IA"}
                  </button>
                )}
                <p className="font-mono-data text-xs text-[#5C5C5C]">
                  O puedes subir tu logo más tarde desde Settings. Generamos uno con la IA basado en tu nombre y industria.
                </p>
              </div>
            )}

            {step === 7 && (
              <div className="space-y-5">
                <label className="flex items-start gap-3 bg-[#FFD600] border border-[#0A0A0A] p-4 cursor-pointer">
                  <input type="checkbox" checked={tax.needs_invoice} onChange={(e) => setT("needs_invoice", e.target.checked)}
                    className="mt-0.5" data-testid="onb-needs-invoice" />
                  <div>
                    <div className="font-mono-mark text-[11px]">SÍ, NECESITO FACTURA</div>
                    <div className="font-mono-data text-xs text-[#0A0A0A]/70">
                      Emitida por <b>Luis Antonio Razo Hernández</b> (persona física) cada vez que pagues tu plan.
                      Si no la necesitas, puedes activarla luego desde tu perfil.
                    </div>
                  </div>
                </label>

                {tax.needs_invoice && (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="onb-tax-fields">
                    <input value={tax.rfc} onChange={(e) => setT("rfc", e.target.value.toUpperCase())} placeholder="RFC *" maxLength={13}
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm uppercase" data-testid="onb-rfc" />
                    <input value={tax.razon_social} onChange={(e) => setT("razon_social", e.target.value)} placeholder="Razón social *"
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" data-testid="onb-razon" />
                    <input value={tax.cp_fiscal} onChange={(e) => setT("cp_fiscal", e.target.value)} placeholder="C.P. fiscal *" maxLength={5}
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" />
                    <select value={tax.regimen_fiscal} onChange={(e) => setT("regimen_fiscal", e.target.value)}
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm">
                      {REGIMEN_FISCAL.map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
                    </select>
                    <select value={tax.uso_cfdi} onChange={(e) => setT("uso_cfdi", e.target.value)}
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm">
                      {USOS_CFDI.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <input value={tax.fiscal_email} onChange={(e) => setT("fiscal_email", e.target.value)} placeholder="Email fiscal (opcional)"
                      className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" type="email" />
                  </div>
                )}
              </div>
            )}

            {step === 8 && (
              <div>
                <div className="bg-[#FFD600] border border-[#0A0A0A] p-5 font-mono-data text-sm space-y-1">
                  <div><b>Marca:</b> {data.brand_name}</div>
                  <div><b>Industria:</b> {data.industry || "—"}</div>
                  <div><b>Audiencia:</b> {data.audience_type} ({data.country})</div>
                  <div><b>Tono:</b> {data.tone} {data.use_emojis ? "· con emojis" : "· sin emojis"}</div>
                  <div><b>Logo:</b> {data.logo_url ? "configurado" : "pendiente"}</div>
                  <div><b>Factura:</b> {tax.needs_invoice ? `sí · ${tax.rfc}` : "no por ahora"}</div>
                </div>
                <p className="font-mono-data text-sm text-[#5C5C5C] mt-5">
                  Te llevamos al generador donde podrás crear tu primer post.
                </p>
              </div>
            )}

            {/* Nav */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#0A0A0A]/15">
              <button onClick={prev} disabled={step === 1}
                className="font-mono-mark text-[10px] px-4 py-2 border border-[#0A0A0A] disabled:opacity-30 flex items-center gap-2"
                data-testid="onb-prev">
                <ArrowLeft className="w-3 h-3" /> ATRÁS
              </button>
              <div className="flex items-center gap-2">
                {step < STEPS.length && (
                  <button onClick={() => finish(true)} className="font-mono-mark text-[10px] text-[#5C5C5C] hover:text-[#0A0A0A]"
                    data-testid="onb-skip">SALTAR</button>
                )}
                {step < STEPS.length ? (
                  <button onClick={next} disabled={!stepValid()}
                    className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-5 py-3 disabled:opacity-30 flex items-center gap-2"
                    data-testid="onb-next">
                    SIGUIENTE <ArrowRight className="w-3 h-3" />
                  </button>
                ) : (
                  <button onClick={() => finish(false)} disabled={saving}
                    className="font-mono-mark text-[10px] bg-[#E53935] text-white px-5 py-3 flex items-center gap-2"
                    data-testid="onb-finish">
                    <Check className="w-3 h-3" /> {saving ? "GUARDANDO..." : "ENTRAR AL DASHBOARD"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
